import cn from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useAccordionContext } from '../AccordionContext';
import { IconChevronDown } from '@/components/icons';

export interface AccordionTitleProps {
	testId?: string;
	panelId: string;
	children: ReactNode;
	className?: string;
}

const AccordionTitle = (props: AccordionTitleProps) => {
	const { testId = 'AccordionTitle', panelId, children, className } = props;
	const { openPanels, onClickHandler } = useAccordionContext();
	// ref for the title span element
	const titleRef = useRef<HTMLSpanElement>(null);
	// boolean state for checking if title is wrapped
	const [isWrapped, setIsWrapped] = useState(false);
	// panel is considered "open" if the context's openPanels set has panelId
	const isOpen = openPanels.has(panelId);

	const handleClick = () => {
		onClickHandler(panelId);
	};

	useEffect(() => {
		// check if title is wrapped
		const checkWrap = () => {
			if (titleRef.current) {
				const lineHeight = parseInt(
					window
						.getComputedStyle(titleRef.current)
						.getPropertyValue('line-height'),
				);
				setIsWrapped(titleRef.current.offsetHeight > lineHeight);
			}
		};

		checkWrap();

		window.addEventListener('resize', checkWrap);
		return () => {
			window.removeEventListener('resize', checkWrap);
		};
	}, []);

	return (
		<button
			data-testid={testId}
			id={`button-${panelId}`}
			aria-expanded={isOpen ? 'true' : 'false'}
			aria-labelledby={`label-${panelId}`}
			aria-controls={`panel-${panelId}`}
			type="button"
			onClick={handleClick}
			className="w-full"
		>
			<div
				className={cn(
					'flex justify-between gap-4 py-4 lg:gap-6 lg:py-5',
					isWrapped ? 'items-start' : 'items-center',
				)}
			>
				<span id={`label-${panelId}`} ref={titleRef} className={className}>
					{children}
				</span>
				<span
					className={cn('h-8 w-8 duration-200 ease-in-out lg:h-10 lg:w-10', {
						'rotate-180': isOpen,
					})}
				>
					<IconChevronDown />
				</span>
			</div>
		</button>
	);
};

export default AccordionTitle;
